import { Component, OnInit } from '@angular/core';
import { MenuItem } from 'primeng/api';
import { APP_CONFIGS, APP_ICONS } from 'app/hub-components/constants/app.constants';
import { Account } from 'app/hub-components/models/account.model';
import { AppInfo } from 'app/hub-components/models/app-info.model';
import { User } from 'app/hub-components/models/user.model';
import { SessionService } from 'app/hub-components/services/session/session.service';
import { TrackingService } from 'app/hub-components/services/tracking/tracking.service';

@Component({
  selector: 'app-menu',
  templateUrl: './menu.component.html',
  styleUrls: ['./menu.component.less'],
})
export class MenuComponent implements OnInit {
  public items: MenuItem[] = [];
  public sidebarVisible: boolean = false;
  public isPublic: boolean = false;
  public hasHelpCenter: boolean = false;
  public user: User;
  public userInitials = '';
  public currentApp: AppInfo;
  public appUrls: { [key: string]: string } = {};
  public currentUrl: string;
  public isBurtAdmin: boolean = false;
  public account: Account;
  public appIcons = APP_ICONS;
  public appConfigs = APP_CONFIGS;
  public showConfigDialog = false;
  public forecastingAppSelected = false;

  private apps: AppInfo[] = [];
  private availableApps: Set<string> = new Set();

  constructor(
    private sessionService: SessionService,
    private trackingService: TrackingService,
  ) { }

  public ngOnInit() {
    this.apps = this.sessionService.apps || [];
    this.user = this.sessionService.user;
    this.account = this.sessionService.account;
    this.isBurtAdmin = this.user?.role === 'burt_admin';
    this.currentApp = this.sessionService.currentApp;
    this.isPublic = this.sessionService.isPublic;

    const userNameSplit = this.user?.name?.trim().split(/\s+/) || [];
    this.userInitials = userNameSplit.map(part => part.charAt(0).toUpperCase()).join('');

    this.initAvailableApps();
    this.initAppUrls();
    this.initMenuItems();
    this.initHelpCenter();
  }

  public openSidebar(event: MouseEvent): void {
    if (event?.target && (event?.target as Element).closest('a')) {
      return;
    }

    event?.stopPropagation();

    this.sidebarVisible = true;
    this.currentUrl = window.location.href.split(':')[1];
  }

  public getAppUrl(app: AppInfo): string {
    if (app.app === 'forecasting' && app.options?.configurations?.[0]?.url) {
      return app.options.configurations[0].url;
    }
    return app.appUrl;
  }

  private initAvailableApps(): void {
    this.availableApps = new Set(this.apps.map(app => app.app));
  }

  private initHelpCenter(): void {
    this.hasHelpCenter = this.availableApps.has('help_center');
  }

  private initAppUrls(): void {
    this.appUrls = this.apps.reduce((urls, app) => {
      urls[app.app] = this.getAppUrl(app);
      return urls;
    }, {});
  }

  public getAppInfoFromMenuItem(item: MenuItem): AppInfo | undefined {
    return this.apps.find(a => a.app === item.label?.toLowerCase());
  }

  public getForecastingApp(): AppInfo | undefined {
    return this.apps.find(app => app.app === 'forecasting');
  }

  public getForecastingConfigurations(): Array<{ url: string; entityName: string }> {
    const forecastingApp = this.getForecastingApp();
    return forecastingApp?.options?.configurations?.map(config => ({
      url: config.url,
      entityName: config.entityName || 'Default',
    })) || [];
  }

  public onStatusButtonClick(): void {
    this.trackingService.track('status button clicked');
  }

  private initMenuItems(): void {
    this.items = APP_CONFIGS
      .filter(config => this.availableApps.has(config.appKey) && config.appKey !== 'help_center')
      .map(config => {
        const app = this.apps.find(a => a.app === config.appKey);
        const isForecastingWithMultipleConfigs = app?.app === 'forecasting' && app.options?.configurations?.length > 1;
        return {
          appKey: config.appKey,
          label: config.label,
          icon: config.icon,
          expanded: true,
          items: config.items?.map(item => ({
            label: item.label,
            url: `${this.appUrls[config.appKey]}${item.url}`,
          })),
          url: isForecastingWithMultipleConfigs ? '#' : this.getAppUrl(app),
          command: isForecastingWithMultipleConfigs ? () => {
            this.forecastingAppSelected = true;
            this.showConfigDialog = true;
          } : undefined,
        };
      });
  }
}
